import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { GroceryItemData } from "@/components/GroceryItem";

export interface HistoryItem {
  id: string;
  date: string;
  goalName: string;
  goal: string;
  items: GroceryItemData[];
  completed: number;
  total: number;
  averageScore: number; // "Trip Grade" - average % match
}

const goalLabels: Record<string, string> = {
  gut: "Gut & Digestion",
  brain: "Brain & Focus",
  heart: "Heart & Cardiovascular",
  energy: "Energy & Vitality",
  immunity: "Immunity & Defense",
  sleep: "Sleep & Recovery",
  bones: "Bones & Joints",
  skin: "Skin & Radiance",
};

export function useGroceryHistory(userId?: string) {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!userId) {
      setHistory([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("user_grocery_lists")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) throw error;

      const formattedHistory: HistoryItem[] = (data || []).map((item: any) => {
        const items: GroceryItemData[] = item.items || [];
        const completed = items.filter((i) => i.completed).length;
        
        // Calculate average score (Trip Grade)
        const scores = items
          .map((i) => i.score)
          .filter((s): s is number => typeof s === "number" && !isNaN(s));
        const averageScore = scores.length > 0
          ? Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length)
          : 0;

        return {
          id: item.id,
          date: new Date(item.created_at).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
          goalName: goalLabels[item.goal] || item.goal,
          goal: item.goal,
          items,
          completed,
          total: items.length,
          averageScore,
        };
      });

      setHistory(formattedHistory);
    } catch (err) {
      console.error("Error fetching history:", err);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const deleteHistoryItem = useCallback(async (listId: string) => {
    if (!userId) return false;
    
    setIsDeleting(listId);
    try {
      const { error } = await supabase
        .from("user_grocery_lists")
        .delete()
        .eq("id", listId)
        .eq("user_id", userId); // Ensure user can only delete their own

      if (error) throw error;

      // Optimistically remove from local state
      setHistory((prev) => prev.filter((item) => item.id !== listId));
      return true;
    } catch (err) {
      console.error("Error deleting history item:", err);
      return false;
    } finally {
      setIsDeleting(null);
    }
  }, [userId]);

  // Clear all history for authenticated users
  const clearAllHistory = useCallback(async () => {
    if (!userId) return false;
    
    setIsDeleting("all");
    try {
      const { error } = await supabase
        .from("user_grocery_lists")
        .delete()
        .eq("user_id", userId);

      if (error) throw error;

      // Clear local state
      setHistory([]);
      return true;
    } catch (err) {
      console.error("Error clearing all history:", err);
      return false;
    } finally {
      setIsDeleting(null);
    }
  }, [userId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return {
    history,
    isLoading,
    isDeleting,
    refreshHistory: fetchHistory,
    deleteHistoryItem,
    clearAllHistory,
  };
}
